import React, { useCallback, useEffect, useState } from 'react';
import api from '../utils/api';
import './UserDetailModal.css';

// UserDetailModal — admin one-shot view of a user.
//
// Props
//   wallet:  the user's wallet address (string, 0x…) OR null to keep closed
//   onClose: () => void
//
// Hits GET /api/admin/user-detail/:wallet — single round-trip that returns
// profile, balances, NFTs, lifetime stats, recent orders/purchases, and any
// open support tickets. Use for triage before refunds, credits, or replies.

const STATUS_COLORS = {
  Pending: '#fbbf24',
  'In progress': '#60a5fa',
  Processing: '#60a5fa',
  Partial: '#f59e0b',
  Completed: '#34d399',
  Canceled: '#f87171',
  Refunded: '#a78bfa',
};

const fmtUsd = (v) => (v == null ? '—' : `$${Number(v).toFixed(2)}`);
const fmtInt = (v) => (v == null ? '—' : new Intl.NumberFormat().format(v));
const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleString(); } catch { return String(d); }
};
const fmtRelative = (d) => {
  if (!d) return '';
  const diff = Date.now() - new Date(d).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const UserDetailModal = ({ wallet, onClose, onSelectOrder }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!wallet) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/admin/user-detail/${wallet}`);
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load user');
    } finally {
      setLoading(false);
    }
  }, [wallet]);

  useEffect(() => {
    if (wallet) load();
    else { setData(null); setError(''); }
  }, [wallet, load]);

  useEffect(() => {
    if (!wallet) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [wallet, onClose]);

  if (!wallet) return null;

  const short = (w) => (w ? `${w.slice(0, 6)}…${w.slice(-4)}` : '—');

  // Smooth-scroll within the modal to a labeled section. Used by the
  // clickable KPI tiles so the admin lands on the relevant rows.
  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="user-detail-modal-overlay" onClick={onClose}>
      <div className="user-detail-modal" onClick={(e) => e.stopPropagation()}>
        <button className="user-detail-close" type="button" onClick={onClose} aria-label="Close">×</button>

        <header className="user-detail-head">
          <span className="user-detail-eyebrow">User Profile</span>
          <h2 className="user-detail-title">{short(wallet)}</h2>
          <code className="user-detail-wallet">{wallet}</code>
          {data?.profile?.is_admin && (
            <span className="user-detail-badge user-detail-badge--admin">ADMIN</span>
          )}
        </header>

        {loading && <div className="user-detail-loading">Loading profile…</div>}
        {error && <div className="user-detail-error">⚠ {error}</div>}

        {data && (
          <>
            {/* KPI strip — balances and lifetime numbers at a glance.
                RULE: every tile is clickable and drills into the source. */}
            <section className="user-detail-kpi-strip">
              <Tile
                label="Vault"
                value={`$${data.balances.vault_usdt}`}
                suffix="USDT"
                onClick={() => window.open(`https://bscscan.com/address/${wallet}#tokentxns`, '_blank', 'noopener')}
                title="Open wallet on BscScan"
              />
              <Tile
                label="Escrow avail."
                value={`$${data.balances.escrow_available_usdt}`}
                suffix="USDT"
                onClick={() => window.open(`https://bscscan.com/address/${wallet}#tokentxns`, '_blank', 'noopener')}
                title="Open wallet on BscScan"
              />
              <Tile
                label="Escrow reserved"
                value={`$${data.balances.escrow_reserved_usdt}`}
                suffix="USDT"
                onClick={() => scrollToSection('udm-orders')}
                title="See orders holding these reserves"
              />
              <Tile
                label="NFTs (chain)"
                value={fmtInt(data.nfts.chain_count)}
                onClick={() => window.open(`https://bscscan.com/token/${process.env.REACT_APP_NFT_CONTRACT_ADDRESS || ''}?a=${wallet}`, '_blank', 'noopener')}
                title="Open NFT holdings on BscScan"
              />
              <Tile
                label="Lifetime orders"
                value={fmtInt(data.lifetime.order_count)}
                onClick={() => scrollToSection('udm-orders')}
                title="Jump to recent orders"
              />
              <Tile
                label="Lifetime spend"
                value={fmtUsd(data.lifetime.order_spend_usd)}
                onClick={() => scrollToSection('udm-orders')}
                title="Jump to recent orders"
              />
              <Tile
                label="Open orders"
                value={fmtInt(data.lifetime.open_orders)}
                accent={data.lifetime.open_orders > 0 ? 'amber' : 'neutral'}
                onClick={() => scrollToSection('udm-orders')}
                title="Jump to recent orders"
              />
              <Tile
                label="Open tickets"
                value={fmtInt(data.open_tickets.length)}
                accent={data.open_tickets.length > 0 ? 'amber' : 'neutral'}
                onClick={() => scrollToSection('udm-tickets')}
                title="Jump to open tickets"
              />
            </section>

            {/* Profile + active discount + NFT IDs */}
            <section className="user-detail-meta">
              <Field label="Member since" value={fmtDate(data.profile.member_since)} />
              <Field label="Last login" value={data.profile.last_login ? `${fmtDate(data.profile.last_login)} (${fmtRelative(data.profile.last_login)})` : '—'} />
              {data.active_discount && (
                <Field
                  label="Active discount"
                  value={
                    <span className="user-detail-discount">
                      {data.active_discount.percent}%
                      {data.active_discount.reason && <span className="user-detail-discount-reason"> · {data.active_discount.reason}</span>}
                      {data.active_discount.expires_at && (
                        <span className="user-detail-discount-meta"> · expires {fmtRelative(data.active_discount.expires_at)}</span>
                      )}
                    </span>
                  }
                  wide
                />
              )}
              {data.nfts.token_ids.length > 0 && (
                <Field
                  label="Token IDs"
                  value={
                    <span className="user-detail-token-ids">
                      {data.nfts.token_ids.map((id) => `#${id}`).join(', ')}
                      {data.nfts.chain_count > data.nfts.token_ids.length && (
                        <span> +{data.nfts.chain_count - data.nfts.token_ids.length} more</span>
                      )}
                    </span>
                  }
                  wide
                />
              )}
            </section>

            {/* Open tickets — most urgent for admin */}
            {data.open_tickets.length > 0 && (
              <section id="udm-tickets" className="user-detail-section">
                <h3 className="user-detail-section-title">Open tickets ({data.open_tickets.length})</h3>
                <ul className="user-detail-list">
                  {data.open_tickets.map((t) => (
                    <li key={t.id} className="user-detail-ticket">
                      <span className="user-detail-ticket-type">{t.type}</span>
                      <span className="user-detail-ticket-body">
                        {t.order_provider_id && <span className="user-detail-ticket-order">#{t.order_provider_id}</span>}
                        {t.reason && <span className="user-detail-ticket-reason"> · {t.reason}</span>}
                      </span>
                      <span className="user-detail-ticket-when">{fmtRelative(t.created_at)}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Recent orders (click → opens OrderDetailModal via callback) */}
            <section id="udm-orders" className="user-detail-section">
              <h3 className="user-detail-section-title">Recent orders</h3>
              {data.recent_orders.length === 0 ? (
                <div className="user-detail-empty">No orders yet.</div>
              ) : (
                <ul className="user-detail-list user-detail-orders">
                  {data.recent_orders.map((o) => (
                    <li
                      key={o.db_id}
                      className={`user-detail-order ${onSelectOrder ? 'user-detail-order--clickable' : ''}`}
                      onClick={onSelectOrder ? () => onSelectOrder(o.db_id) : undefined}
                      role={onSelectOrder ? 'button' : undefined}
                      tabIndex={onSelectOrder ? 0 : undefined}
                      onKeyDown={onSelectOrder ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelectOrder(o.db_id); }
                      } : undefined}
                    >
                      <span
                        className="user-detail-order-status"
                        style={{
                          color: STATUS_COLORS[o.status] || '#aaa',
                          background: (STATUS_COLORS[o.status] || '#aaa') + '15',
                          borderColor: (STATUS_COLORS[o.status] || '#aaa') + '55',
                        }}
                      >
                        {o.status}
                      </span>
                      <span className="user-detail-order-svc" title={o.service_name}>
                        {o.service_name || '—'}
                      </span>
                      <span className="user-detail-order-qty">{fmtInt(o.quantity)}</span>
                      <span className="user-detail-order-amt">{fmtUsd(o.charged_usd)}</span>
                      <span className="user-detail-order-when">{fmtRelative(o.created_at)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* Recent store purchases */}
            {data.recent_purchases.length > 0 && (
              <section className="user-detail-section">
                <h3 className="user-detail-section-title">Recent store purchases</h3>
                <ul className="user-detail-list">
                  {data.recent_purchases.map((p) => (
                    <li key={p.id} className="user-detail-purchase">
                      <span className="user-detail-purchase-title" title={p.product_title}>
                        {p.product_title || '—'}
                      </span>
                      <span className="user-detail-purchase-amt">
                        {p.amount} {p.currency || ''}
                      </span>
                      <span className="user-detail-purchase-when">{fmtRelative(p.purchased_at)}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const Tile = ({ label, value, suffix, accent = 'gold', onClick, title }) => {
  const clickable = typeof onClick === 'function';
  const Cmp = clickable ? 'button' : 'div';
  return (
    <Cmp
      type={clickable ? 'button' : undefined}
      onClick={clickable ? onClick : undefined}
      title={title}
      className={`user-detail-tile user-detail-tile--${accent} ${clickable ? 'user-detail-tile--clickable' : ''}`}
    >
      <span className="user-detail-tile-label">{label}</span>
      <span className="user-detail-tile-value">
        {value}
        {suffix && <span className="user-detail-tile-suffix"> {suffix}</span>}
      </span>
    </Cmp>
  );
};

const Field = ({ label, value, wide }) => (
  <div className={`user-detail-field ${wide ? 'user-detail-field--wide' : ''}`}>
    <span className="user-detail-field-label">{label}</span>
    <span className="user-detail-field-value">{value}</span>
  </div>
);

export default UserDetailModal;
